import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Input,
    Button,
    Text,
    Flex,
    Heading,
    Grid,
    useBreakpointValue,
    useTheme,
    Select,
    ButtonGroup,
    HStack,
    Checkbox,
    VStack,
    FormControl,
} from '@chakra-ui/react';
import { MdClear } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';
import { WaterLevelFilterState } from '../types/waterLevel';
import { getTodayDateString, getDateNDaysAgo } from '../utils/waterLevel';
import WaterLevelStats from '../waterLevel/WaterLevelStats';
import { useWaterLevel } from '../hooks/useWaterLevel';

interface FilterWaterLevelProps {
    filters: WaterLevelFilterState;
    onFilterChange: (filters: WaterLevelFilterState) => void;
    onClearFilters: () => void;
    onApplyFilters: () => void;
}

const FilterWaterLevel: React.FC<FilterWaterLevelProps> = ({
    filters,
    onFilterChange,
    onClearFilters,
    onApplyFilters,
}) => {
    const theme = useTheme();
    const [localFilters, setLocalFilters] = useState<WaterLevelFilterState>(filters);
    const footerGap = useBreakpointValue({ base: 2, md: 3 });
    const { filterOptions } = useWaterLevel();

    // Style constants matching Filter.tsx
    const inputStyles = {
        size: "sm" as const,
        borderRadius: "lg",
        borderColor: "gray.200",
        _hover: { borderColor: "gray.300" },
        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    // Sync local filters when filters prop changes
    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleInputChange = useCallback((field: keyof WaterLevelFilterState, value: string | number | boolean | undefined) => {
        const newFilters = { ...localFilters, [field]: value };
        setLocalFilters(newFilters);
        // REMOVED: onFilterChange(newFilters); - Only update local state, don't trigger global change
    }, [localFilters]);

    const handleApplyFilters = useCallback(() => {
        onFilterChange(localFilters);
        onApplyFilters();
    }, [localFilters, onFilterChange, onApplyFilters]);

    const handleClearFilters = useCallback(() => {
        const clearedFilters: WaterLevelFilterState = {
            start_date: getDateNDaysAgo(7),
            end_date: getTodayDateString(),
            category_id: 12, // Default to category id 12
            dashboard_type: localFilters.dashboard_type || 'water_level'
        };
        setLocalFilters(clearedFilters);
        onFilterChange(clearedFilters);
        onClearFilters();
    }, [localFilters.dashboard_type, onFilterChange, onClearFilters]);

    const handleDashboardTypeChange = (type: 'water_level' | 'curah_hujan') => {
        const newFilters = {
            ...localFilters,
            dashboard_type: type,
            category_id: type === 'curah_hujan' ? 15 : 12 // Auto set category based on dashboard type
        };
        setLocalFilters(newFilters);
    };

    // Handle WMS status checkboxes
    const handleWmsStatusChange = (status: string, checked: boolean) => {
        let newWmsStatusArray = localFilters.wms_status_array || [];

        if (checked) {
            // Add status if not already in array
            if (!newWmsStatusArray.includes(status)) {
                newWmsStatusArray = [...newWmsStatusArray, status];
            }
        } else {
            // Remove status from array
            newWmsStatusArray = newWmsStatusArray.filter(s => s !== status);
        }

        const newFilters = {
            ...localFilters,
            wms_status_array: newWmsStatusArray,
            // Also disable inspection points when WMS status points are shown
            show_inspection_points: newWmsStatusArray.length > 0 ? false : localFilters.show_inspection_points
        };
        setLocalFilters(newFilters);
    };

    // Handle inspection points change
    const handleInspectionPointsChange = (checked: boolean) => {
        const newFilters = {
            ...localFilters,
            show_inspection_points: checked,
            // Clear WMS status array when inspection points is enabled
            wms_status_array: checked ? [] : localFilters.wms_status_array
        };
        setLocalFilters(newFilters);
    };

    // Filter categories based on dashboard type
    const availableCategories = filterOptions.categories.filter(category => {
        if (localFilters.dashboard_type === 'curah_hujan') {
            return category.id === 15; // Only Curah Hujan category
        }
        return category.id !== 15; // All except Curah Hujan
    });


    // Filter estates based on selected company
    const availableEstates = localFilters.company_id
        ? filterOptions.estates.filter(estate => Number(estate.company_id) === Number(localFilters.company_id))
        : filterOptions.estates;

    // Filter divisis based on selected estate
    const availableDivisis = localFilters.estate_id
        ? filterOptions.divisis.filter(divisi => divisi.estate_id === localFilters.estate_id)
        : filterOptions.divisis;

    // Check if any WMS status is selected
    const hasWmsStatusSelected = localFilters.wms_status_array && localFilters.wms_status_array.length > 0;

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            bg="white"
        >
            {/* Header Section - Fixed */}
            <Flex
                as="header"
                position="sticky"
                top={0}
                bg="white"
                zIndex={2}
                px={useBreakpointValue({ base: 3, md: 4 })}
                py={useBreakpointValue({ base: 2, md: 0 })}
                align="center"
                justify="center"
            >
                <Heading size={useBreakpointValue({ base: "sm", md: "md" })}>
                    Water Level Dashboard
                </Heading>
            </Flex>

            {/* Scrollable Content */}
            <Box
                flex={1}
                overflowY="auto"
                px={useBreakpointValue({ base: 3, md: 4 })}
                py={useBreakpointValue({ base: 3, md: 4 })}
                position="relative"
            >
                <Grid templateColumns="1fr" gap={useBreakpointValue({ base: 3, md: 4 })}>
                    {/* Dashboard Type Selection */}
                    <Box>
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                            Tipe Dashboard
                        </Text>
                        <ButtonGroup isAttached width="100%" size="sm">
                            <Button
                                flex={1}
                                variant={localFilters.dashboard_type === 'water_level' ? 'solid' : 'outline'}
                                colorScheme="blue"
                                onClick={() => handleDashboardTypeChange('water_level')}
                            >
                                Water Level
                            </Button>
                            <Button
                                flex={1}
                                variant={localFilters.dashboard_type === 'curah_hujan' ? 'solid' : 'outline'}
                                colorScheme="blue"
                                onClick={() => handleDashboardTypeChange('curah_hujan')}
                            >
                                Curah Hujan
                            </Button>
                        </ButtonGroup>
                    </Box>

                    {/* Season Selection (Only for Water Level) */}
                    {localFilters.dashboard_type === 'water_level' && (
                        <Box>
                            <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                                Musim
                            </Text>
                            <Select
                                value={localFilters.season || 'normal'}
                                onChange={(e) => handleInputChange('season', e.target.value)}
                                {...inputStyles}
                            >
                                <option value="normal">Normal</option>
                                <option value="kemarau">Kemarau</option>
                            </Select>
                        </Box>
                    )}

                    {/* Date Range */}
                    <Flex gap={useBreakpointValue({ base: 2, md: 3 })} direction={{ base: "column", sm: "row" }}>
                        <Box flex={1}>
                            <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                                Dari Tanggal
                            </Text>
                            <Input
                                type="date"
                                value={localFilters.start_date || ''}
                                onChange={(e) => handleInputChange('start_date', e.target.value)}
                                {...inputStyles}
                            />
                        </Box>
                        <Box flex={1}>
                            <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                                Sampai Tanggal
                            </Text>
                            <Input
                                type="date"
                                value={localFilters.end_date || ''}
                                onChange={(e) => handleInputChange('end_date', e.target.value)}
                                {...inputStyles}
                            />
                        </Box>
                    </Flex>

                    {/* Company Filter */}
                    <Box>
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                            Company
                        </Text>
                        <Select
                            placeholder="Semua Company"
                            value={localFilters.company_id || ''}
                            onChange={(e) => handleInputChange('company_id', e.target.value ? parseInt(e.target.value) : undefined)}
                            {...inputStyles}
                        >
                            {filterOptions.companies.map((company) => (
                                <option key={company.id} value={company.id}>
                                    {company.name}
                                </option>
                            ))}
                        </Select>
                    </Box>

                    {/* Estate Filter
                    <Box>
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                            Estate
                        </Text>
                        <Select
                            placeholder="Pilih Estate"
                            value={localFilters.estate_id || ''}
                            onChange={(e) => handleInputChange('estate_id', e.target.value ? parseInt(e.target.value) : undefined)}
                            {...inputStyles}
                        >
                            {filterOptions.estates.map((estate) => (
                                <option key={estate.id} value={estate.id}>
                                    {estate.name}
                                </option>
                            ))}
                        </Select>
                    </Box> */}

                    {/* Estate Filter */}
                    <Box>
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                            Estate
                        </Text>
                        <Select
                            placeholder="Pilih Estate"
                            value={localFilters.estate_id || ''}
                            onChange={(e) => handleInputChange('estate_id', e.target.value ? parseInt(e.target.value) : undefined)}
                            {...inputStyles}
                            isDisabled={!localFilters.company_id}
                        >
                            {availableEstates.map((estate) => (
                                <option key={estate.id} value={estate.id}>
                                    {estate.name}
                                </option>
                            ))}
                        </Select>
                    </Box>

                    {/* Divisi Filter */}
                    <Box>
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                            Divisi
                        </Text>
                        <Select
                            placeholder="Pilih Divisi"
                            value={localFilters.divisi_id || ''}
                            onChange={(e) => handleInputChange('divisi_id', e.target.value ? parseInt(e.target.value) : undefined)}
                            {...inputStyles}
                            isDisabled={!localFilters.estate_id}
                        >
                            {availableDivisis.map((divisi) => (
                                <option key={divisi.id} value={divisi.id}>
                                    {divisi.name}
                                </option>
                            ))}
                        </Select>
                    </Box>

                    {/* Category Filter */}
                    <Box>
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                            Kategori
                        </Text>
                        <Select
                            value={localFilters.category_id || 12}
                            onChange={(e) => handleInputChange('category_id', e.target.value ? parseInt(e.target.value) : 12)}
                            {...inputStyles}
                        >
                            {availableCategories.map((category) => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Select>
                    </Box>

                    {/* Show Inspection Points */}
                    <Box>
                        <FormControl display="flex" alignItems="center">
                            <Checkbox
                                id="show-inspection-points"
                                isChecked={localFilters.show_inspection_points || false}
                                onChange={(e) => handleInspectionPointsChange(e.target.checked)}
                                colorScheme="blue"
                                size="sm"
                                isDisabled={hasWmsStatusSelected} // Disabled when WMS status is selected
                            >
                                <Text
                                    fontSize="sm"
                                    color={hasWmsStatusSelected ? "gray.400" : "gray.600"}
                                >
                                    Tampilkan Titik Inspeksi
                                </Text>
                            </Checkbox>
                        </FormControl>
                    </Box>

                    {/* WMS Status Filter - Checkboxes */}
                    <Box>
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                            Status WMS
                        </Text>
                        <VStack spacing={2} align="start">
                            <Checkbox
                                isChecked={localFilters.wms_status_array?.includes('k') || false}
                                onChange={(e) => handleWmsStatusChange('k', e.target.checked)}
                                colorScheme="orange"
                                size="sm"
                                isDisabled={localFilters.show_inspection_points} // Disabled when inspection points is enabled
                            >
                                <Text
                                    fontSize="sm"
                                    color={localFilters.show_inspection_points ? "gray.400" : "gray.600"}
                                >
                                    Kotor
                                </Text>
                            </Checkbox>
                            <Checkbox
                                isChecked={localFilters.wms_status_array?.includes('r') || false}
                                onChange={(e) => handleWmsStatusChange('r', e.target.checked)}
                                colorScheme="red"
                                size="sm"
                                isDisabled={localFilters.show_inspection_points} // Disabled when inspection points is enabled
                            >
                                <Text
                                    fontSize="sm"
                                    color={localFilters.show_inspection_points ? "gray.400" : "gray.600"}
                                >
                                    Rusak
                                </Text>
                            </Checkbox>
                        </VStack>
                    </Box>

                    {/* Stats Component */}
                    {/* <Box>
                        <Box 
                            p={3} 
                            borderRadius="lg" 
                            border="1px solid" 
                            borderColor="gray.200"
                            bg="gray.50"
                        >
                            <WaterLevelStats isSatellite={false} />
                        </Box>
                    </Box> */}
                </Grid>
            </Box>

            {/* Fixed Footer */}
            <HStack
                bg="white"
                zIndex={3}
                px="16px"
                pt="16px"
                justify="flex-end"
                spacing={footerGap}
                flexShrink={0}
                sx={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}
            >
                <Button
                    variant="outline"
                    colorScheme="red"
                    size="sm"
                    onClick={handleClearFilters}
                    leftIcon={<MdClear />}
                    width={{ base: "full", sm: "auto" }}
                >
                    Hapus Filter
                </Button>

                <Button
                    colorScheme="green"
                    size="sm"
                    onClick={handleApplyFilters}
                    leftIcon={<FiSearch />}
                    width={{ base: "full", sm: "auto" }}
                >
                    Terapkan Filter
                </Button>
            </HStack>
        </Box>
    );
};

export default FilterWaterLevel;